import Breadcrumb from '../components/Breadcrumb';
import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect, useRef } from 'react';
import { WRAP, SECTION_PAD } from '../styles/layout';
import { Helmet } from 'react-helmet-async';

const C = '#10B981';
const R = '16,185,129';
const BG = '#09060F';

/* ─── Breakpoint hook ───────────────────────────────────────────────────────
   mob  = < 768px   (phone)
   tab  = < 1024px  (tablet / narrow desktop)
   ─────────────────────────────────────────────────────────────────────────── */
function useBreakpoint() {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1280);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return { mob: w < 768, tab: w < 1024 };
}

function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function Counter({ to, prefix = '', suffix = '', duration = 1800 }) {
  const [val, setVal] = useState(0);
  const [ref, visible] = useReveal(0.3);
  const started = useRef(false);
  useEffect(() => {
    if (!visible || started.current) return;
    started.current = true;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(ease * to));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [visible, to, duration]);
  return <span ref={ref}>{prefix}{val.toLocaleString()}{suffix}</span>;
}

function Reveal({ children, delay = 0, style = {} }) {
  const [ref, visible] = useReveal();
  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'none' : 'translateY(24px)',
      transition: `opacity .75s ease ${delay}ms, transform .75s ease ${delay}ms`,
      ...style,
    }}>{children}</div>
  );
}

const Section = ({ children, style = {} }) => (
  <section style={{ padding: `${SECTION_PAD} 0`, position: 'relative', ...style }}>
    {children}
  </section>
);

const Cap = ({ children, style = {} }) => (
  <div style={{ ...WRAP, ...style }}>{children}</div>
);

const Eyebrow = ({ children }) => (
  <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, letterSpacing:3, textTransform:'uppercase', color:C, margin:'0 0 18px', opacity:.85 }}>{children}</p>
);

const H2 = ({ children, style = {} }) => (
  <h2 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:800, lineHeight:1.1, fontSize:'clamp(30px,3vw,48px)', color:'#EDE9F8', margin:'0 0 20px', letterSpacing:'-1px', ...style }}>{children}</h2>
);

const Body = ({ children, style = {} }) => (
  <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:'clamp(14px,1.1vw,17px)', fontWeight:300, lineHeight:1.8, color:'#9B90BA', margin:'0 0 16px', ...style }}>{children}</p>
);

/* ── LIVE DASHBOARD PANEL ── */
function LiveDashboard() {
  const [tick, setTick] = useState(0);
  const [events, setEvents] = useState([
    { id:1, type:'blocked',    msg:'Prompt injection blocked',        agent:'CustomerService-v2',  time:'just now' },
    { id:2, type:'quarantine', msg:'Shadow agent quarantined',        agent:'UnknownLangChain-7',  time:'12s ago'  },
    { id:3, type:'approved',   msg:'Policy check passed',             agent:'RevSec-Monitor',      time:'28s ago'  },
    { id:4, type:'discovery',  msg:'New MCP server discovered',       agent:'dev-repo/payments',   time:'1m ago'   },
    { id:5, type:'approved',   msg:'Human approval granted',          agent:'DataPipeline-v3',     time:'2m ago'   },
  ]);

  const agents = [
    { name:'CustomerService-v2',  framework:'LangChain',  status:'monitored',   risk:12 },
    { name:'DataPipeline-v3',     framework:'CrewAI',     status:'approved',    risk:8  },
    { name:'UnknownLangChain-7',  framework:'LangChain',  status:'quarantined', risk:94 },
    { name:'BillingAgent-v1',     framework:'AutoGen',    status:'monitored',   risk:31 },
  ];

  useEffect(() => {
    const newEvents = [
      { type:'blocked',    msg:'Prompt injection blocked',     agent:'BillingAgent-v1'   },
      { type:'discovery',  msg:'Shadow AI tool detected',      agent:'employee@corp.com' },
      { type:'quarantine', msg:'PII access without oversight', agent:'ReportGen-beta'    },
      { type:'approved',   msg:'Policy sweep completed',       agent:'6 agents checked'  },
    ];
    const id = setInterval(() => {
      setTick(t => t + 1);
      setEvents(prev => {
        const next = newEvents[Math.floor(Math.random() * newEvents.length)];
        return [{ ...next, id: Date.now(), time: 'just now' }, ...prev.slice(0, 4)];
      });
    }, 4000);
    return () => clearInterval(id);
  }, []);

  const statusColor = s => s === 'quarantined' ? '#EF4444' : s === 'approved' ? C : `rgba(${R},.55)`;
  const eventColor  = t => t === 'blocked' || t === 'quarantine' ? '#EF4444' : t === 'discovery' ? '#F59E0B' : C;
  const eventIcon   = t => t === 'blocked' || t === 'quarantine' ? '⚠' : t === 'discovery' ? '⊕' : '✓';

  return (
    <div style={{
      background:'rgba(255,255,255,.03)',
      border:'1px solid rgba(255,255,255,.08)',
      borderRadius:16,
      overflow:'hidden',
      animation:'fadeUp .9s ease both .5s',
      boxShadow:`0 32px 80px rgba(0,0,0,.6), 0 0 60px rgba(${R},.06)`,
      fontFamily:"'DM Sans',system-ui,sans-serif",
    }}>
      {/* Title bar */}
      <div style={{ padding:'12px 18px', background:'rgba(255,255,255,.04)', borderBottom:'1px solid rgba(255,255,255,.07)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ display:'flex', gap:5 }}>
            {['#EF4444','#F59E0B','#10B981'].map((c,i) => <div key={i} style={{ width:9, height:9, borderRadius:'50%', background:c, opacity:.7 }}/>)}
          </div>
          <span style={{ fontSize:11, fontWeight:600, color:'rgba(200,192,236,0.55)', letterSpacing:.5 }}>RevSec — AI Security Operations</span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <div style={{ width:6, height:6, borderRadius:'50%', background:C, animation:'pulse 2s infinite' }}/>
          <span style={{ fontSize:10, fontWeight:700, color:`rgba(${R},.8)`, letterSpacing:1, textTransform:'uppercase' }}>Live</span>
        </div>
      </div>

      {/* Agent inventory */}
      <div style={{ padding:'14px 18px 10px' }}>
        <div style={{ fontSize:10, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:'rgba(200,192,236,0.38)', marginBottom:10 }}>Agent Inventory — 4 Active</div>
        <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
          {agents.map((a,i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 10px', background:'rgba(255,255,255,.025)', borderRadius:7, border:'1px solid rgba(255,255,255,.05)' }}>
              <div style={{ width:6, height:6, borderRadius:'50%', background:statusColor(a.status), flexShrink:0, boxShadow: a.status==='quarantined'?'0 0 6px #EF4444':a.status==='approved'?`0 0 6px ${C}`:'none' }}/>
              <span style={{ fontSize:12, fontWeight:500, color:'#DDD8F2', flex:1, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.name}</span>
              <span style={{ fontSize:10, color:'rgba(200,192,236,0.45)', flexShrink:0 }}>{a.framework}</span>
              <div style={{ width:48, height:4, borderRadius:4, background:'rgba(255,255,255,.07)', flexShrink:0, overflow:'hidden' }}>
                <div style={{ height:'100%', borderRadius:4, width:`${a.risk}%`, background: a.risk > 70 ? '#EF4444' : a.risk > 40 ? '#F59E0B' : C, transition:'width 1s ease' }}/>
              </div>
              <span style={{ fontSize:10, fontWeight:700, color: a.risk > 70 ? '#EF4444' : a.risk > 40 ? '#F59E0B' : `rgba(${R},.7)`, flexShrink:0, minWidth:22, textAlign:'right' }}>{a.risk}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ height:1, background:'rgba(255,255,255,.06)', margin:'4px 0' }}/>

      {/* Live event feed */}
      <div style={{ padding:'10px 18px 16px' }}>
        <div style={{ fontSize:10, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:'rgba(200,192,236,0.38)', marginBottom:10 }}>Live Event Feed</div>
        <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
          {events.map((e, i) => (
            <div key={e.id} style={{
              display:'flex', alignItems:'flex-start', gap:10,
              padding:'7px 10px', borderRadius:7,
              background: i === 0 ? `rgba(${e.type==='blocked'||e.type==='quarantine'?'239,68,68':e.type==='discovery'?'245,158,11':'16,185,129'},.07)` : 'transparent',
              border: i === 0 ? `1px solid rgba(${e.type==='blocked'||e.type==='quarantine'?'239,68,68':e.type==='discovery'?'245,158,11':'16,185,129'},.2)` : '1px solid transparent',
              transition:'all .4s ease',
              animation: i === 0 ? 'fadeUp .4s ease both' : 'none',
            }}>
              <span style={{ fontSize:11, color:eventColor(e.type), flexShrink:0, marginTop:1 }}>{eventIcon(e.type)}</span>
              <div style={{ flex:1, minWidth:0 }}>
                <span style={{ fontSize:12, fontWeight:600, color:'#DDD8F2' }}>{e.msg} </span>
                <span style={{ fontSize:11, color:'rgba(200,192,236,0.55)' }}>— {e.agent}</span>
              </div>
              <span style={{ fontSize:10, color:'rgba(200,192,236,0.38)', flexShrink:0, marginTop:1 }}>{e.time}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Footer stats */}
      <div style={{ padding:'10px 18px 14px', borderTop:'1px solid rgba(255,255,255,.06)', display:'flex', gap:0 }}>
        {[['47','Tools found'],['12','Shadow AI'],['6','Policies active'],['0','Breaches']].map(([val,label],i) => (
          <div key={i} style={{ flex:1, textAlign:'center', borderRight: i<3?'1px solid rgba(255,255,255,.06)':'' }}>
            <div style={{ fontSize:16, fontWeight:800, color: label==='Breaches'?C:label==='Shadow AI'?'#F59E0B':'#DDD8F2', fontFamily:"'Fraunces',Georgia,serif", lineHeight:1 }}>{val}</div>
            <div style={{ fontSize:9, fontWeight:600, color:'rgba(200,192,236,0.4)', letterSpacing:.5, marginTop:3, textTransform:'uppercase' }}>{label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── HERO ── */
function Hero() {
  const navigate = useNavigate();
  const { mob, tab } = useBreakpoint();

  return (
    <section style={{ background:BG, display:'flex', alignItems:'center', padding:`${SECTION_PAD} 0 clamp(40px,5vh,60px)`, position:'relative', overflow:'hidden' }}>
      <div aria-hidden="true" style={{ position:'absolute', inset:0, pointerEvents:'none' }}>
        <div style={{ position:'absolute', top:'-10%', right:'-5%', width:700, height:700, borderRadius:'50%', background:`radial-gradient(circle,rgba(${R},.1) 0%,transparent 65%)`, filter:'blur(90px)' }}/>
        <div style={{ position:'absolute', bottom:'0%', left:'-10%', width:600, height:600, borderRadius:'50%', background:'radial-gradient(circle,rgba(45,24,84,.6) 0%,transparent 65%)', filter:'blur(80px)' }}/>
        <div style={{ position:'absolute', inset:0, backgroundImage:'linear-gradient(rgba(255,255,255,.018) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.018) 1px,transparent 1px)', backgroundSize:'80px 80px', maskImage:'radial-gradient(ellipse 80% 60% at 70% 50%,black 0%,transparent 100%)' }}/>
      </div>

      <div style={{ ...WRAP, position:'relative', zIndex:2, width:'100%',
        display:'grid',
        gridTemplateColumns: mob ? '1fr' : '1fr 1fr',
        gap: mob ? '40px' : 'clamp(40px,5vw,80px)',
        alignItems:'center',
      }}>

        {/* LEFT: copy */}
        <div>
          <Breadcrumb
            color={C}
            items={[
              { label: 'Home', path: '/' },
              { label: 'AI Roles', path: '/' },
              { label: 'RevSec — AI CISO' },
            ]}
          />
          <div style={{ display:'inline-flex', alignItems:'center', gap:10, padding:'7px 16px 7px 12px', borderRadius:100, marginBottom:32, background:`rgba(${R},.08)`, border:`1px solid rgba(${R},.2)`, animation:'fadeUp .7s ease both .1s', flexWrap:'wrap' }}>
            <div style={{ width:6, height:6, borderRadius:'50%', background:C, animation:'pulse 2s infinite', flexShrink:0 }}/>
            <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:12, fontStyle:'italic', color:`rgba(${R},.9)`, fontWeight:400 }}>You secured your endpoints. You secured your identities. Who's securing your AI?</span>
          </div>
          <h1 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, lineHeight:1.04, letterSpacing:'-2px', fontSize: mob ? '38px' : 'clamp(38px,4.2vw,72px)', color:'#EDE9F8', margin:'0 0 24px', animation:'fadeUp .8s ease both .2s' }}>
            Your AI Never Sleeps.<br/><span style={{ color:C }}>Neither Does Ours.</span>
          </h1>
          <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:'clamp(15px,1.15vw,18px)', fontWeight:300, lineHeight:1.8, color:'#9B90BA', maxWidth:520, margin:'0 0 40px', animation:'fadeUp .7s ease both .35s' }}>
            RevSec is the agentic security layer for enterprise AI — discovering what AI exists in your organization, governing what it's allowed to do, and monitoring what it actually does.{' '}
            <strong style={{ color:'#DDD8F2', fontWeight:400 }}>Autonomously.</strong>
          </p>
          <div style={{ display:'flex', gap:12, flexWrap:'wrap', animation:'fadeUp .7s ease both .48s' }}>
            <button style={{ padding:'14px 30px', borderRadius:10, cursor:'pointer', background:C, color:'#EDE9F8', border:'none', fontSize:15, fontWeight:700, fontFamily:"'DM Sans',system-ui,sans-serif", boxShadow:`0 6px 28px rgba(${R},.4)`, transition:'all .22s ease', flex: mob ? '1 1 auto' : 'none' }}
              onClick={()=>{ window.scrollTo(0,0); navigate('/contact'); }}
              onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.boxShadow=`0 12px 36px rgba(${R},.55)`;}}
              onMouseLeave={e=>{e.currentTarget.style.transform='none';e.currentTarget.style.boxShadow=`0 6px 28px rgba(${R},.4)`;}}
            >Request a Demo</button>
            <button style={{ padding:'14px 24px', borderRadius:10, cursor:'pointer', background:'transparent', color:'#C8C0EC', border:'1px solid rgba(255,255,255,.12)', fontSize:15, fontWeight:400, fontFamily:"'DM Sans',system-ui,sans-serif", transition:'all .22s ease', flex: mob ? '1 1 auto' : 'none' }}
              onClick={()=>document.getElementById('how-it-works')?.scrollIntoView({behavior:'smooth'})}
              onMouseEnter={e=>{e.currentTarget.style.borderColor='rgba(255,255,255,.28)';e.currentTarget.style.color='rgba(255,255,255,.82)';}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor='rgba(255,255,255,.12)';e.currentTarget.style.color='rgba(255,255,255,.5)';}}
            >See How It Works ↓</button>
          </div>
          <div style={{ display:'flex', gap:20, marginTop:44, flexWrap:'wrap', animation:'fadeUp .7s ease both .6s' }}>
            {['< 1 hr deploy','17 policies','< 5ms detection','EU AI Act ready'].map((t,i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:6 }}>
                <div style={{ width:4, height:4, borderRadius:'50%', background:C, opacity:.6 }}/>
                <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11.5, color:'rgba(200,192,236,0.55)' }}>{t}</span>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT: live dashboard — hidden on mobile to avoid overcrowding */}
        {!mob && (
          <div style={{ position:'relative' }}>
            <div aria-hidden="true" style={{ position:'absolute', inset:'-20%', background:`radial-gradient(ellipse 70% 60% at 50% 50%,rgba(${R},.12) 0%,transparent 70%)`, filter:'blur(30px)', pointerEvents:'none' }}/>
            <LiveDashboard/>
          </div>
        )}

      </div>
    </section>
  );
}

/* ── PROVOCATION ── */
function Provocation() {
  const [ref, visible] = useReveal(0.3);
  return (
    <div id="how-it-works" ref={ref} style={{ background:`rgba(${R},.04)`, borderTop:`1px solid rgba(${R},.1)`, borderBottom:`1px solid rgba(${R},.1)`, padding:'clamp(40px,5vh,60px) clamp(24px,4vw,72px)', textAlign:'center' }}>
      <p style={{ fontFamily:"'Fraunces',Georgia,serif", fontStyle:'italic', fontWeight:300, fontSize:'clamp(18px,2.2vw,32px)', lineHeight:1.5, color:'#DDD8F2', margin:0, opacity:visible?1:0, transform:visible?'none':'translateY(16px)', transition:'opacity .9s ease, transform .9s ease' }}>
        "Your AI agents make 10,000 decisions a day.<br/><span style={{ color:C }}>How many did your security team review?"</span>
      </p>
    </div>
  );
}

/* ── PROBLEMS ── */
function Problems() {
  const problems = [
    { n:'01', headline:'Shadow AI Is Already Inside Your Organization', body:"68% of employees use AI tools on personal accounts. Your security team doesn't know which tools, what data is going in, or what's coming back. Every unapproved ChatGPT conversation with customer data is a breach waiting to happen — and you can't stop what you can't see.", stat:'1 in 5', statDetail:'AI-related breaches caused by shadow AI. Each costs $670,000 more than a standard breach.', source:'IBM Cost of a Data Breach 2025' },
    { n:'02', headline:'Your AI Agents Are Running Unsupervised', body:"Enterprises are deploying autonomous AI agents that query databases, send emails, process payments, and make decisions — with no centralised visibility, no governance, and no audit trail. These agents operate at machine speed with human-level permissions. If one is compromised, the blast radius is your entire data estate.", stat:'150 GB', statDetail:'stolen by a solo hacker using a jailbroken AI across 6 government agencies over 30 days. Nobody detected it.', source:'CrowdStrike 2026' },
    { n:'03', headline:"Your Security Stack Wasn't Built for AI", body:"CrowdStrike watches your endpoints. Okta manages your identities. Palo Alto secures your network. None of them can tell you which AI agents exist, what MCP servers they're connected to, or whether a prompt injection attack is happening right now. AI is the fourth attack surface — and it's the one nobody is watching.", stat:'97%', statDetail:'of organizations that experienced an AI-related breach had no AI access controls in place.', source:'IBM Cost of a Data Breach 2025' },
  ];
  return (
    <Section style={{ background:BG, borderTop:'1px solid rgba(255,255,255,.05)' }}>
      <Cap>
        <Reveal><Eyebrow>The Problem</Eyebrow><H2>AI Is Your Newest Attack Surface —<br/>And It's Completely Unmonitored</H2></Reveal>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:2, marginTop:56 }}>
          {problems.map((p,i) => (
            <Reveal key={i} delay={i*120}>
              <div style={{ padding:'40px 36px', background:'rgba(255,255,255,.02)', border:'1px solid rgba(255,255,255,.06)', height:'100%', cursor:'default', transition:'background .2s ease' }}
                onMouseEnter={e=>e.currentTarget.style.background=`rgba(${R},.04)`}
                onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,.02)'}
              >
                <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:13, fontWeight:700, color:`rgba(${R},.35)`, letterSpacing:1, marginBottom:20 }}>{p.n}</div>
                <h3 style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:'clamp(18px,1.6vw,22px)', fontWeight:700, color:'#DDD8F2', lineHeight:1.3, margin:'0 0 16px' }}>{p.headline}</h3>
                <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:14, fontWeight:300, lineHeight:1.75, color:'#9B90BA', margin:'0 0 32px' }}>{p.body}</p>
                <div style={{ padding:'20px', background:`rgba(${R},.06)`, borderLeft:`3px solid rgba(${R},.5)`, borderRadius:'0 8px 8px 0' }}>
                  <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:'clamp(28px,2.5vw,36px)', fontWeight:900, color:C, lineHeight:1, marginBottom:8 }}>{p.stat}</div>
                  <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:12.5, fontWeight:400, lineHeight:1.6, color:'#C8C0EC', margin:'0 0 6px' }}>{p.statDetail}</p>
                  <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:600, letterSpacing:.5, color:`rgba(${R},.5)`, margin:0, textTransform:'uppercase' }}>— {p.source}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Cap>
    </Section>
  );
}

/* ── SOLUTION ── */
function Solution() {
  const { mob, tab } = useBreakpoint();
  const caps = [
    {
      id:'discover', label:'01 — Discover',
      headline:'Find Every AI Tool, Agent, and MCP Server — Including the Ones Nobody Told You About',
      intro:"Your employees are using AI tools your security team doesn't know about. Your developers are deploying agents that aren't in any inventory. Your AI agents are connected to MCP servers that were never reviewed. RevSec finds all of it.",
      bullets:[
        { title:'Shadow AI Detection', body:'Real-time browser-level monitoring identifies every AI tool employees interact with — ChatGPT, Claude, Gemini, Copilot, Perplexity, and more — without disrupting workflows.' },
        { title:'AI Agent Discovery', body:'Automated scanning of GitHub, AWS Bedrock, Azure AI, and GCP Vertex detects AI agents built on LangChain, CrewAI, AutoGen, Semantic Kernel, and 7 other frameworks.' },
        { title:'MCP Server Inventory', body:'Discovers every MCP server connection across your AI infrastructure — what tools they expose, what data they access, whether they use authentication, and which agents are connected.' },
        { title:'Continuous Discovery', body:'Automated daily scans catch new tools, agents, and MCP servers as they appear. No manual inventory. No stale spreadsheets. Always current.' },
      ],
      outcome:'Go from "we think we have 15 AI tools" to "we know we have 47, and 12 are shadow."',
    },
    {
      id:'govern', label:'02 — Govern',
      headline:'Enforce Security Policies Automatically — Before Risk Becomes a Breach',
      intro:"Knowing what AI exists is only the first step. RevSec enforces your security policies across every AI asset automatically. No manual reviews. No governance gaps. Policies run continuously and violations are caught the moment they occur.",
      bullets:[
        { title:'Agent IAM — Active Directory for AI', body:'Define what your AI agents and MCP servers can and cannot do. 17 pre-built governance policies mapped to EU AI Act, NIST AI RMF, SOC 2, HIPAA, and ISO 27001 — active from day one.' },
        { title:'Automated Enforcement', body:"An agent accessing PII without human oversight? Auto-quarantined. An MCP server with no authentication? Blocked. A shadow agent deployed by a developer? Flagged for review. In real time, not quarterly audits." },
        { title:'Human-in-the-Loop Approvals', body:"High-risk actions route through your security team via Slack, Microsoft Teams, or email. Auto-escalation to the CISO if reviewers don't respond. Nothing falls through the cracks." },
        { title:'Risk in Dollars, Not Colours', body:'Every AI asset gets a risk score using FAIR methodology — annual loss expectancy in actual dollars your CFO can act on. Not red/yellow/green.' },
      ],
      outcome:"A new shadow agent is discovered at 2 AM, auto-evaluated against 17 policies, and quarantined with a Slack alert to your security lead — before the developer's morning standup.",
    },
    {
      id:'monitor', label:'03 — Monitor & Protect',
      headline:'AI Security Agents That Watch Your AI Agents — 24/7, Autonomously',
      intro:"RevSec doesn't wait for you to check a dashboard. Our AI security agents continuously analyse agent behaviour, detect attacks, and take action — while your team sleeps.",
      bullets:[
        { title:'Runtime Prompt Injection Detection', body:'A two-layer detection engine catches prompt injection attacks in real time. Fast pattern matching (47 attack signatures, <5ms) handles known techniques. An AI judge analyses ambiguous cases for deeper reasoning.' },
        { title:'Continuous Agent Monitoring', body:'Every tool call, every data access, every LLM invocation, every decision your AI agents make — logged, classified, and analysed. Our SDK captures it all with two lines of code and zero performance impact.' },
        { title:'Autonomous Background Operations', body:'Six security tasks run continuously without human intervention: hourly policy sweeps, heartbeat monitoring, HITL timeout processing, risk score recalculation, stale agent detection, and scheduled discovery scans.' },
        { title:'Complete Audit Trail', body:'Every action, every decision, every policy evaluation, every alert — immutably logged. Who approved what, when, and why. Full trail for compliance reviews, incident investigations, and regulatory reporting.' },
      ],
      outcome:'A prompt injection attack targets your customer service agent at 3 AM. RevSec detects it in under 5 milliseconds, blocks the input, alerts your team via Slack, and logs the complete event for forensics.',
    },
  ];

  const [active, setActive] = useState('discover');
  const cap = caps.find(c => c.id === active);

  return (
    <Section style={{ background:`rgba(${R},.025)`, borderTop:`1px solid rgba(${R},.08)`, borderBottom:`1px solid rgba(${R},.08)` }}>
      <Cap>
        <Reveal>
          <Eyebrow>The Solution</Eyebrow>
          <H2>One Platform. Complete AI Security.</H2>
          <Body style={{ maxWidth:580 }}>RevSec gives your security team complete visibility and control over every AI tool, every AI agent, and every MCP server in your organization — from employee AI usage to autonomous agent operations.</Body>
        </Reveal>

        {/* Tabs — scrollable on mobile */}
        <div style={{
          display:'flex', gap:4, marginTop:48, marginBottom:40,
          borderBottom:'1px solid rgba(255,255,255,.07)', paddingBottom:0,
          overflowX: mob ? 'auto' : 'visible',
          WebkitOverflowScrolling: 'touch',
        }}>
          {caps.map(c => (
            <button key={c.id} onClick={()=>setActive(c.id)} style={{
              padding: mob ? '12px 16px' : '12px 24px',
              cursor:'pointer', background:'none', border:'none',
              borderBottom:`2px solid ${active===c.id?C:'transparent'}`, marginBottom:-1,
              fontFamily:"'DM Sans',system-ui,sans-serif",
              fontSize: mob ? 12.5 : 13.5,
              fontWeight:active===c.id?700:400,
              color:active===c.id?C:'rgba(255,255,255,.35)',
              transition:'all .2s ease',
              whiteSpace:'nowrap', flexShrink:0,
            }}>{c.label}</button>
          ))}
        </div>

        <div key={active} style={{ animation:'fadeIn .4s ease both' }}>
          <div style={{
            display:'grid',
            gridTemplateColumns: tab ? '1fr' : '1fr 1fr',
            gap: tab ? '40px' : '64px',
            alignItems:'start',
          }}>
            <div>
              <h3 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:800, fontSize:'clamp(20px,1.8vw,28px)', lineHeight:1.25, color:'#EDE9F8', margin:'0 0 20px' }}>{cap.headline}</h3>
              <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:15, fontWeight:300, lineHeight:1.8, color:'#9B90BA', margin:'0 0 32px' }}>{cap.intro}</p>
              <div style={{ padding:'20px 24px', background:`rgba(${R},.08)`, border:`1px solid rgba(${R},.2)`, borderRadius:10 }}>
                <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:`rgba(${R},.7)`, margin:'0 0 10px' }}>Business Outcome</p>
                <p style={{ fontFamily:"'Fraunces',Georgia,serif", fontStyle:'italic', fontSize:15, fontWeight:300, lineHeight:1.7, color:'#C8C0EC', margin:0 }}>"{cap.outcome}"</p>
              </div>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
              {cap.bullets.map((b,i) => (
                <div key={i} style={{ padding:'20px 22px', background:'rgba(255,255,255,.025)', border:'1px solid rgba(255,255,255,.07)', borderRadius:10, transition:'all .2s ease', animation:`fadeUp .5s ease ${i*80}ms both`, cursor:'default' }}
                  onMouseEnter={e=>{e.currentTarget.style.background=`rgba(${R},.06)`;e.currentTarget.style.borderColor=`rgba(${R},.2)`;}}
                  onMouseLeave={e=>{e.currentTarget.style.background='rgba(255,255,255,.025)';e.currentTarget.style.borderColor='rgba(255,255,255,.07)';}}
                >
                  <div style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
                    <div style={{ width:6, height:6, borderRadius:'50%', background:C, marginTop:6, flexShrink:0 }}/>
                    <div>
                      <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13.5, fontWeight:700, color:'#D4CEF0', margin:'0 0 5px' }}>{b.title}</p>
                      <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, fontWeight:300, lineHeight:1.7, color:'#9B90BA', margin:0 }}>{b.body}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Cap>
      <style>{`@keyframes fadeIn{from{opacity:0}to{opacity:1}}`}</style>
    </Section>
  );
}

/* ── HOW IT WORKS ── */
function HowItWorks() {
  const steps = [
    { n:'1', time:'Under 1 hour', headline:'Connect Your Environment', body:"Connect GitHub, AWS, and Azure through RevSec's integrations page. Deploy our browser extension for employee AI monitoring. Install our Python SDK on your AI agents with two lines of code. All connections use your existing credentials — no new infrastructure required.", detail:'pip install revsec' },
    { n:'2', time:'Hours 1–4', headline:'See Everything', body:"RevSec immediately scans your environment and surfaces every AI tool, agent, and MCP server. Shadow tools on personal accounts. LangChain agents buried in developer repos. MCP servers exposing database access without authentication. Each gets a risk score, compliance assessment, and governance status.", detail:'Typical finding: organizations discover 3× more AI tools than expected.' },
    { n:'3', time:'Day one — ongoing', headline:'Govern and Protect Automatically', body:"17 pre-built policies activate immediately. High-risk agents get flagged. Unauthenticated MCP servers get blocked. Prompt injection attacks get caught and stopped. From this point forward, RevSec runs autonomously. New agents discovered overnight are policy-checked before sunrise.", detail:'From deployment to governed: same day.' },
  ];
  return (
    <Section style={{ background:BG }}>
      <Cap>
        <Reveal><Eyebrow>How It Works</Eyebrow><H2>Up and Running in Hours, Not Months</H2></Reveal>
        <div style={{ marginTop:64, position:'relative' }}>
          <div style={{ position:'absolute', left:27, top:40, bottom:40, width:1, background:`rgba(${R},.15)` }}/>
          {steps.map((s,i) => (
            <Reveal key={i} delay={i*150}>
              <div style={{ display:'flex', gap:40, padding:'0 0 52px', position:'relative' }}>
                <div style={{ width:54, height:54, borderRadius:'50%', flexShrink:0, background:`rgba(${R},.12)`, border:`1.5px solid rgba(${R},.4)`, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'Fraunces',Georgia,serif", fontSize:18, fontWeight:800, color:C, position:'relative', zIndex:1, boxShadow:`0 0 20px rgba(${R},.15)` }}>{s.n}</div>
                <div style={{ paddingTop:10 }}>
                  <div style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'3px 10px', borderRadius:100, marginBottom:12, background:`rgba(${R},.08)`, border:`1px solid rgba(${R},.18)` }}>
                    <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:10.5, fontWeight:700, color:C, letterSpacing:.5 }}>{s.time}</span>
                  </div>
                  <h3 style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:'clamp(18px,1.6vw,24px)', fontWeight:700, color:'#DDD8F2', margin:'0 0 12px' }}>{s.headline}</h3>
                  <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:14.5, fontWeight:300, lineHeight:1.8, color:'#9B90BA', margin:'0 0 14px', maxWidth:600 }}>{s.body}</p>
                  <div style={{ display:'inline-block', padding:'6px 14px', borderRadius:7, background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.08)', fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:12, fontStyle:'italic', color:`rgba(${R},.75)` }}>{s.detail}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Cap>
    </Section>
  );
}

/* ── NUMBERS ── */
function Numbers() {
  const { mob, tab } = useBreakpoint();
  const threat = [
    { n:89, suffix:'%', label:'increase in AI-enabled attacks year-over-year', source:'CrowdStrike 2026' },
    { n:97, suffix:'%', label:'of AI-breached organizations had no AI access controls in place', source:'IBM 2025' },
    { n:670, prefix:'$', suffix:'K', label:'additional cost per shadow AI breach vs. a standard breach', source:'IBM 2025' },
    { n:57, suffix:'%', label:'prompt injection success rate even with safeguards on MCP surfaces', source:'Anthropic 2026' },
  ];
  const deliver = [
    { val:'< 1 Hour', label:'from deployment to first AI discovery scan' },
    { val:'17', label:'pre-built governance policies active on day one' },
    { val:'< 5ms', label:'prompt injection detection response time' },
    { val:'24/7', label:'autonomous monitoring with zero human intervention' },
  ];
  return (
    <Section style={{ background:`rgba(${R},.03)`, borderTop:`1px solid rgba(${R},.08)`, borderBottom:`1px solid rgba(${R},.08)` }}>
      <Cap>
        <div style={{
          display:'grid',
          gridTemplateColumns: tab ? '1fr' : '1fr 1fr',
          gap: tab ? '60px' : '80px',
        }}>
          <div>
            <Reveal><Eyebrow>The AI Threat Landscape in 2026</Eyebrow><H2 style={{ fontSize:'clamp(22px,2vw,28px)', marginBottom:36 }}>The numbers your board needs to see.</H2></Reveal>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:2 }}>
              {threat.map((t,i) => (
                <Reveal key={i} delay={i*80}>
                  <div style={{ padding:'28px 24px', background:'rgba(0,0,0,.25)', border:'1px solid rgba(255,255,255,.06)', height:'100%' }}>
                    <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:'clamp(30px,2.8vw,44px)', fontWeight:900, color:C, lineHeight:1, marginBottom:10 }}>
                      <Counter to={t.n} prefix={t.prefix||''} suffix={t.suffix}/>
                    </div>
                    <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:12.5, fontWeight:300, lineHeight:1.65, color:'#9B90BA', margin:'0 0 8px' }}>{t.label}</p>
                    <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:10, fontWeight:700, letterSpacing:.8, color:`rgba(${R},.45)`, textTransform:'uppercase', margin:0 }}>— {t.source}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <div>
            <Reveal><Eyebrow>What RevSec Delivers</Eyebrow><H2 style={{ fontSize:'clamp(22px,2vw,28px)', marginBottom:36 }}>Built to perform from day one.</H2></Reveal>
            <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
              {deliver.map((d,i) => (
                <Reveal key={i} delay={i*80}>
                  <div style={{ padding:'24px 28px', background:`rgba(${R},.06)`, border:`1px solid rgba(${R},.15)`, display:'flex', alignItems:'center', gap:24 }}>
                    <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:'clamp(22px,2vw,30px)', fontWeight:900, color:C, lineHeight:1, flexShrink:0, minWidth:90 }}>{d.val}</div>
                    <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13.5, fontWeight:300, lineHeight:1.6, color:'#C8C0EC', margin:0 }}>{d.label}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </Cap>
    </Section>
  );
}

/* ── COMPLIANCE ── */
function Compliance() {
  const frameworks = [
    { name:'EU AI Act', sub:'Automated risk classification · Article 12–15 mapping · documentation generation for regulatory submissions' },
    { name:'NIST AI RMF', sub:'Map, Measure, Manage, Govern functions addressed across the full agent lifecycle with continuous evidence collection' },
    { name:'SOC 2 Type II', sub:'Access controls, change management, and security monitoring with complete audit trails for every agent action' },
    { name:'HIPAA', sub:'PHI access controls for AI agents in healthcare. Data classification, access logging, and automatic policy enforcement' },
    { name:'ISO 27001 & 42001', sub:'Information security and AI management system controls. MCP server authentication enforcement and continuous risk assessment' },
    { name:'OWASP LLM Top 10', sub:'Runtime detection for prompt injection (LLM01), sensitive information disclosure (LLM06), and excessive agency (LLM08)' },
  ];
  return (
    <Section style={{ background:BG }}>
      <Cap>
        <Reveal>
          <Eyebrow>Compliance & Frameworks</Eyebrow>
          <H2>Built for the Frameworks Your Auditors Require</H2>
          <Body style={{ maxWidth:560 }}>Every RevSec policy, assessment, and report maps directly to the compliance frameworks that govern AI usage in regulated industries. A single assessment covers multiple frameworks simultaneously — no duplicate work.</Body>
        </Reveal>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))', gap:12, marginTop:48 }}>
          {frameworks.map((f,i) => (
            <Reveal key={i} delay={i*60}>
              <div style={{ padding:'24px', background:'rgba(255,255,255,.025)', border:'1px solid rgba(255,255,255,.07)', borderRadius:10, transition:'all .2s ease', cursor:'default' }}
                onMouseEnter={e=>{e.currentTarget.style.background=`rgba(${R},.06)`;e.currentTarget.style.borderColor=`rgba(${R},.25)`;}}
                onMouseLeave={e=>{e.currentTarget.style.background='rgba(255,255,255,.025)';e.currentTarget.style.borderColor='rgba(255,255,255,.07)';}}
              >
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
                  <div style={{ width:28, height:28, borderRadius:7, background:`rgba(${R},.15)`, border:`1px solid rgba(${R},.3)`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={C} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  </div>
                  <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13.5, fontWeight:700, color:'#D4CEF0' }}>{f.name}</span>
                </div>
                <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:12.5, fontWeight:300, lineHeight:1.7, color:'#9B90BA', margin:0 }}>{f.sub}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Cap>
    </Section>
  );
}

/* ── WHO IT'S FOR ── */
function WhoItsFor() {
  const { mob, tab } = useBreakpoint();
  const [active, setActive] = useState(0);
  const personas = [
    { role:'The CISO', icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>, headline:"Your board is asking about AI risk. RevSec gives you the answer.", body:"A single pane of glass across every AI asset in your organization — with risk quantified in dollars, compliance mapped to frameworks, and enforcement running autonomously. The next time your board asks \"how are we governing AI?\" you open RevSec and show them.", value:"Board-ready AI risk posture · Compliance evidence · Autonomous enforcement" },
    { role:'Security Operations', icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>, headline:"AI security on autopilot. No new dashboards. Alerts in your existing channels.", body:"You're already overwhelmed with alerts from endpoints, identity, and cloud. RevSec handles AI security autonomously so your team doesn't add it to their workload. Alerts flow to your existing Slack channels. Violations appear in your SIEM. When something needs human judgment, RevSec escalates.", value:"AI security on autopilot · No new dashboards · Alerts in existing channels" },
    { role:'Governance, Risk & Compliance', icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>, headline:"EU AI Act enforcement is live. Your auditors are asking. RevSec has the evidence.", body:"RevSec maps every AI asset to EU AI Act, NIST AI RMF, SOC 2, HIPAA, and ISO 42001 automatically. Every policy evaluation, every approval decision, every violation resolution is logged with who, when, and why. Export compliance reports for audit cycles without manual evidence gathering.", value:"Continuous compliance evidence · Multi-framework mapping · Audit-ready documentation" },
    { role:'AI & Platform Engineering', icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>, headline:"Your agents ship faster because the security process is automated.", body:"Security review is a black hole that takes weeks. RevSec gives your agents a clear path to approved status. Install the SDK (two lines of code, <50ms overhead), register your agent, and let governance work. Compliant agents get approved fast. Risky configurations get flagged with specific, actionable feedback — not a blanket \"no.\"", value:"Faster time to production · Clear governance path · Two-line SDK integration" },
  ];
  const p = personas[active];

  return (
    <Section style={{ background:`rgba(${R},.025)`, borderTop:`1px solid rgba(${R},.08)`, borderBottom:`1px solid rgba(${R},.08)` }}>
      <Cap>
        <Reveal><Eyebrow>Who It's For</Eyebrow><H2>Built for the Teams Responsible for AI Risk</H2></Reveal>

        {tab ? (
          /* ── Tablet/mobile: stacked tab buttons + content below ── */
          <div style={{ marginTop:40 }}>
            <div style={{
              display:'flex', gap:8, flexWrap:'wrap', marginBottom:32,
            }}>
              {personas.map((per,i) => (
                <button key={i} onClick={()=>setActive(i)} style={{
                  display:'flex', alignItems:'center', gap:8, padding:'10px 16px',
                  cursor:'pointer', borderRadius:8,
                  background: active===i ? `rgba(${R},.08)` : 'rgba(255,255,255,.03)',
                  border: `1px solid ${active===i ? `rgba(${R},.3)` : 'rgba(255,255,255,.08)'}`,
                  fontFamily:"'DM Sans',system-ui,sans-serif",
                  fontSize:12.5, fontWeight:active===i?700:400,
                  color:active===i?C:'#9B90BA',
                  transition:'all .2s ease',
                }}>
                  <span style={{ color:active===i?C:'rgba(200,192,236,0.55)' }}>{per.icon}</span>
                  <span>{per.role}</span>
                </button>
              ))}
            </div>
            <div key={active} style={{ padding:'28px 24px', background:'rgba(255,255,255,.025)', border:'1px solid rgba(255,255,255,.07)', borderRadius:10, animation:'fadeIn .35s ease both' }}>
              <h3 style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:'clamp(18px,1.8vw,24px)', fontWeight:700, color:'#EDE9F8', margin:'0 0 16px', lineHeight:1.3 }}>{p.headline}</h3>
              <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:15, fontWeight:300, lineHeight:1.8, color:'#9B90BA', margin:'0 0 24px' }}>{p.body}</p>
              <div style={{ padding:'14px 20px', background:`rgba(${R},.08)`, border:`1px solid rgba(${R},.18)`, borderRadius:8 }}>
                <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', color:`rgba(${R},.7)`, margin:'0 0 4px' }}>Key Value</p>
                <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, color:`rgba(${R},.85)`, margin:0 }}>{p.value}</p>
              </div>
            </div>
          </div>
        ) : (
          /* ── Desktop: sidebar + content ── */
          <div style={{ display:'grid', gridTemplateColumns:'220px 1fr', gap:2, marginTop:52 }}>
            <div style={{ display:'flex', flexDirection:'column' }}>
              {personas.map((per,i) => (
                <button key={i} onClick={()=>setActive(i)} style={{ display:'flex', alignItems:'center', gap:12, padding:'16px 20px', cursor:'pointer', background: active===i?`rgba(${R},.06)`:'transparent', border:'none', textAlign:'left', borderLeft:`2px solid ${active===i?C:'rgba(255,255,255,.08)'}`, transition:'all .2s ease' }}>
                  <span style={{ color:active===i?C:'rgba(200,192,236,0.55)', flexShrink:0 }}>{per.icon}</span>
                  <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:12.5, fontWeight:active===i?700:400, color:active===i?'#EDE9F8':'#9B90BA', transition:'color .2s ease' }}>{per.role}</span>
                </button>
              ))}
            </div>
            <div key={active} style={{ padding:'32px 40px', background:'rgba(255,255,255,.025)', border:'1px solid rgba(255,255,255,.07)', animation:'fadeIn .35s ease both' }}>
              <h3 style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:'clamp(20px,1.8vw,26px)', fontWeight:700, color:'#EDE9F8', margin:'0 0 18px', lineHeight:1.3 }}>{p.headline}</h3>
              <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:15, fontWeight:300, lineHeight:1.8, color:'#9B90BA', margin:'0 0 28px' }}>{p.body}</p>
              <div style={{ padding:'14px 20px', background:`rgba(${R},.08)`, border:`1px solid rgba(${R},.18)`, borderRadius:8 }}>
                <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', color:`rgba(${R},.7)`, margin:'0 0 4px' }}>Key Value</p>
                <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, color:`rgba(${R},.85)`, margin:0 }}>{p.value}</p>
              </div>
            </div>
          </div>
        )}
      </Cap>
    </Section>
  );
}

/* ── WHY REVSEC ── */
function WhyRevSec() {
  const diffs = [
    { headline:'Agentic by Design', body:"Most AI security tools give you a dashboard and wait for you to look at it. RevSec deploys its own AI security agents that autonomously discover, evaluate, monitor, and enforce — 24/7. Our AI watches your AI so your security team can focus on what requires human judgment." },
    { headline:'Full Stack, Not Point Solution', body:"Other platforms focus on one slice: model red-teaming, or data encryption, or compliance checklists. RevSec covers the complete AI lifecycle — from an employee's first ChatGPT conversation to an autonomous agent's 10,000th database query to an MCP server's authentication posture. One platform, one policy engine, one audit trail." },
    { headline:'Operational, Not Theoretical', body:"RevSec doesn't just assess risk — it acts on it. Auto-quarantine risky agents. Block prompt injection attacks in under 5 milliseconds. Escalate approval requests through your chain of command. Generate compliance reports on demand. The platform doesn't just tell you something is wrong. It does something about it." },
    { headline:'Enterprise-Ready Today', body:"SSO via Azure AD. Multi-tenant architecture. Role-based access. Encrypted at rest and in transit. Deployment on your preferred cloud. RevSec meets enterprise procurement requirements because it was built for enterprise deployment from day one — not retrofitted from a research project." },
  ];
  return (
    <Section style={{ background:BG }}>
      <Cap>
        <Reveal>
          <Eyebrow>Why RevSec</Eyebrow>
          <H2 style={{ maxWidth:680 }}>We Didn't Start from Security and Try to Understand AI.</H2>
          <p style={{ fontFamily:"'Fraunces',Georgia,serif", fontStyle:'italic', fontWeight:300, fontSize:'clamp(16px,1.4vw,20px)', lineHeight:1.7, color:'#9B90BA', margin:'0 0 60px', maxWidth:680 }}>We started from building AI agents and understood what security they actually need.</p>
        </Reveal>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))', gap:2 }}>
          {diffs.map((d,i) => (
            <Reveal key={i} delay={i*80}>
              <div style={{ padding:'36px 32px', height:'100%', borderLeft:`3px solid ${i===0?`rgba(${R},.4)`:'rgba(255,255,255,.06)'}`, background:i===0?`rgba(${R},.04)`:'rgba(255,255,255,.015)', transition:'all .2s ease', cursor:'default' }}
                onMouseEnter={e=>{e.currentTarget.style.borderLeftColor=`rgba(${R},.5)`;e.currentTarget.style.background=`rgba(${R},.05)`;}}
                onMouseLeave={e=>{e.currentTarget.style.borderLeftColor=i===0?`rgba(${R},.4)`:'rgba(255,255,255,.06)';e.currentTarget.style.background=i===0?`rgba(${R},.04)`:'rgba(255,255,255,.015)';}}
              >
                <h3 style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:'clamp(17px,1.5vw,21px)', fontWeight:700, color:'#DDD8F2', margin:'0 0 14px' }}>{d.headline}</h3>
                <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13.5, fontWeight:300, lineHeight:1.8, color:'#9B90BA', margin:0 }}>{d.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={200}>
          <div style={{ marginTop:56, padding:'32px 36px', background:`rgba(${R},.05)`, border:`1px solid rgba(${R},.15)`, borderRadius:14, display:'flex', gap:24, alignItems:'flex-start' }}>
            <div style={{ width:3, alignSelf:'stretch', background:C, borderRadius:2, flexShrink:0 }}/>
            <p style={{ fontFamily:"'Fraunces',Georgia,serif", fontStyle:'italic', fontWeight:300, fontSize:'clamp(14px,1.2vw,17px)', lineHeight:1.75, color:'#C8C0EC', margin:0 }}>RevSec was built by the team behind RevUp AI, which has spent three years building enterprise AI agents for healthcare, financial services, and consulting. Every capability in RevSec was born from a real problem we encountered deploying AI in production.</p>
          </div>
        </Reveal>
      </Cap>
    </Section>
  );
}

/* ── CTA ── */
function CTA() {
  const { mob } = useBreakpoint();
  return (
    <section style={{ background:`linear-gradient(135deg,rgba(${R},.12) 0%,rgba(45,24,84,.8) 60%,${BG} 100%)`, borderTop:`1px solid rgba(${R},.15)`, padding:`${SECTION_PAD} 0`, textAlign:'center', position:'relative', overflow:'hidden' }}>
      <div aria-hidden="true" style={{ position:'absolute', top:'-20%', left:'50%', transform:'translateX(-50%)', width:600, height:600, borderRadius:'50%', background:`radial-gradient(circle,rgba(${R},.15) 0%,transparent 65%)`, filter:'blur(80px)', pointerEvents:'none' }}/>
      <div style={{ position:'relative', zIndex:1 }}>
        <Reveal>
          <Eyebrow>Get Started</Eyebrow>
          <h2 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:'clamp(32px,4vw,60px)', lineHeight:1.08, letterSpacing:'-1.5px', color:'#EDE9F8', margin:'0 auto 20px', maxWidth:700 }}>See What Your AI Is Doing</h2>
          <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:'clamp(15px,1.2vw,18px)', fontWeight:300, lineHeight:1.75, color:'#9B90BA', margin:'0 auto 48px', maxWidth:480 }}>Most organizations discover 3× more AI tools than they thought they had. Find out what's running in yours.</p>
          <div style={{ display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap', padding: mob ? '0 16px' : '0' }}>
            <button style={{ padding:'16px 36px', borderRadius:10, cursor:'pointer', background:C, color:'#EDE9F8', border:'none', fontSize:16, fontWeight:700, fontFamily:"'DM Sans',system-ui,sans-serif", boxShadow:`0 8px 32px rgba(${R},.45)`, transition:'all .22s ease', flex: mob ? '1 1 auto' : 'none' }}
              onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.boxShadow=`0 14px 44px rgba(${R},.6)`;}}
              onMouseLeave={e=>{e.currentTarget.style.transform='none';e.currentTarget.style.boxShadow=`0 8px 32px rgba(${R},.45)`;}}
            >Request a Demo</button>
            <button style={{ padding:'16px 28px', borderRadius:10, cursor:'pointer', background:'rgba(255,255,255,.06)', color:'#C8C0EC', border:'1px solid rgba(255,255,255,.14)', fontSize:16, fontWeight:400, fontFamily:"'DM Sans',system-ui,sans-serif", transition:'all .22s ease', flex: mob ? '1 1 auto' : 'none' }}
              onMouseEnter={e=>{e.currentTarget.style.background='rgba(255,255,255,.1)';e.currentTarget.style.color='rgba(255,255,255,.88)';}}
              onMouseLeave={e=>{e.currentTarget.style.background='rgba(255,255,255,.06)';e.currentTarget.style.color='rgba(255,255,255,.6)';}}
            >Talk to Our Team</button>
            <button style={{ padding:'16px 24px', borderRadius:10, cursor:'pointer', background:'transparent', color:`rgba(${R},.75)`, border:`1px solid rgba(${R},.25)`, fontSize:14, fontWeight:400, fontFamily:"'DM Sans',system-ui,sans-serif", transition:'all .22s ease', width: mob ? '100%' : 'auto' }}
              onMouseEnter={e=>{e.currentTarget.style.background=`rgba(${R},.08)`;e.currentTarget.style.color=C;}}
              onMouseLeave={e=>{e.currentTarget.style.background='transparent';e.currentTarget.style.color=`rgba(${R},.75)`;}}
            >↓ Download Platform Overview</button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function AICISO() {
  return (
    <>
      <Helmet>
        <title>AI CISO — RevSec | AI Agent Security, Red Teaming & NIST AI RMF | RevUpAI</title>
        <meta name="description" content="RevSec is your AI CISO — securing AI agents against red team attacks and prompt injection, mapping your posture to NIST AI RMF, and discovering every shadow AI tool in 48 hours. EU AI Act, SOC2, and HIPAA compliance built in. Operated inside your enterprise." />
        <meta name="keywords" content="AI CISO, AI agent security, AI red teaming, NIST AI RMF, NIST AI framework, shadow AI discovery, EU AI Act compliance, AI governance enterprise, SOC2 AI compliance, AI agent governance, HIPAA AI compliance, prompt injection detection, LLM security enterprise, agentic AI security, AI risk management, MCP server security" />
        <link rel="canonical" href="https://revupai.com/ai-ciso" />
        <meta property="og:title" content="AI CISO — AI Agent Security, Red Teaming & NIST AI RMF | RevUpAI" />
        <meta property="og:description" content="AI agent security, red teaming, NIST AI RMF mapping, and shadow AI discovery in 48 hours. Operated inside your enterprise." />
        <meta property="og:url" content="https://revupai.com/ai-ciso" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://revupai.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AI CISO — AI Agent Security & NIST AI RMF | RevUpAI" />
        <meta name="twitter:description" content="AI agent security, red teaming, NIST AI RMF, shadow AI discovery in 48 hours." />
        <meta name="twitter:image" content="https://revupai.com/og-image.png" />
      </Helmet>
      <style>{`
        @keyframes fadeUp  { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:none} }
        @keyframes fadeIn  { from{opacity:0} to{opacity:1} }
        @keyframes pulse   { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.4;transform:scale(.8)} }
        * { box-sizing:border-box; }
      `}</style>
      <div style={{ background:BG, minHeight:'100vh' }}>
        <Hero/>
        <Provocation/>
        <Problems/>
        <Solution/>
        <HowItWorks/>
        <Numbers/>
        <Compliance/>
        <WhoItsFor/>
        <WhyRevSec/>
        <CTA/>
      </div>
    </>
  );
}